/**
 * Fehler:
 * Wenn erst ein neues Projekt geöffnet wird und dann ein existierendes, geht alles wie es soll
 * Wenn man erst ein existierendes und dann ein neues Projekt öffnet, wird der Name vom existierend geändert wenn im neuen was eigegeben wird
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Container, Form, Input, InputNumber, Message, Panel, Schema, Stack } from 'rsuite'
import { useTranslation } from 'react-i18next'
import Format, { DateField, DisableOverlay, SelectField, TextField } from '@/client/components/FormControls'
import ProjectModel, { EProjectStatus, projectStatusList } from '@client/models/project'
import { useLayout } from '@/client/components/Layout/LayoutManager'
import {
    TTabListFormData,
    TabListFooterStyle,
    TabListFooterErrorWrapperStyle,
    TabListFooterErrorStyle,
    Button,
} from '@client/components/Controls'
import ClientList from './ClientList'
import UserProjectRelation, { RelationType } from '@/client/components/Relation/UserProjectRelation'
import { PanelError } from '@client/components/ErrorProvider'
import { Capabilities, hasPermission, RoleType } from '@/shared'
import error from '@client/stores/errorStore'
import tabStore, { findTab, setTabName } from '@client/stores/tabsStore'
import { useSyncedState } from '@client/utils/useSyncedState.ts'
import { serverLog } from '@client/utils/serverLog.ts'
import { user } from '@client/stores/authStore.ts'
import toastMessage from '@client/utils/toastMessage'
import { useEditLock } from '@client/socket/useEditLock.tsx'
import { useTabDirty, useTabSaveHandler } from '@client/utils/useTabDirty.ts'
import { getSocket } from '@client/socket/socket.ts'

const { StringType, ArrayType, BooleanType } = Schema.Types

const projectModel = new ProjectModel()

type EditProjectProps = { tabId: string; onCreated?: (newProject: any) => void }

const EditProject: React.FC<EditProjectProps> = ({ tabId, onCreated }) => {
    const { t } = useTranslation()

    const { cmd } = useLayout()
    const format = Format()
    const formRef = useRef<TTabListFormData>(null)
    const [formError, setFormError] = useState<string | null>(null)
    const tab = findTab(tabId)
    if (!tab) return null
    const [formData, setFormData] = useSyncedState(tab, 'formData', tab.store.initialData)
    const dirty = useMemo(() => {
        if (!tab?.store?.initialData) return false
        return JSON.stringify(formData) !== JSON.stringify(tab.store.initialData)
    }, [formData, tab])

    useTabDirty(tab, dirty)

    useTabSaveHandler(tab, () => onSubmit({ preventDefault() { } } as any))

    const newRecord = !tab?.store?.id
    const readOnly = !hasPermission(Capabilities.project_write)
    const refetch = async () => {
        if (!tab.store.id) return
        const res: any = await projectModel.read(String(tab.store.id))
        if (res?.data) {
            const next = { ...(res.data || {}) }
            setFormData(next)
            tab.store.initialData = next
        }
    }
    const lock = useEditLock({
        table: 'project',
        url: 'project',
        id: String(tab?.store?.id || ''),
        tabId,
        userId: String(user.value?._id || ''),
        userName: String(user.value?.userName || ''),
        enabled: !newRecord,
        onAcquire: refetch,
        onUpdated: refetch,
    })

    // The archive (operator, 14.10.2026): an archived project is read-only, and
    // only an administrator brings it back. Into the archive only from completed,
    // by owner, project leader or administrator. The server checks the same.
    const storedStatus = tab.store.initialData?.status
    const archived = storedStatus === EProjectStatus.Archived
    const role = user.value?.role
    const isAdmin = role === RoleType.Administrator
    const mayArchive = [RoleType.Administrator, RoleType.Owner, RoleType.ProjectLeader].includes(role)
    const statusList = projectStatusList().filter(
        (item) =>
            item.value !== EProjectStatus.Archived ||
            storedStatus === EProjectStatus.Archived ||
            (storedStatus === EProjectStatus.Completed && mayArchive)
    )

    const canEdit = !readOnly && !archived && (newRecord || lock.status === 'owned')

    const onRestore = async () => {
        const response = await projectModel.update(String(tab.store.id), { status: EProjectStatus.Completed })
        if (!response?.data) return
        toastMessage(t('Successfully saved'))
        await refetch()
        getSocket().emit('editing client: resourceUpdated', {
            table: 'project',
            id: String(tab.store.id),
            url: 'project',
        })
    }

    const archiveBanner = archived ? (
        <div style={{ marginBottom: '1em' }}>
            <Message type="info" showIcon>
                {t('This project is archived and read-only.')}
                {isAdmin && (
                    <div data-editlock-allow style={{ marginTop: '0.5em' }}>
                        <Button appearance="ghost" onClick={onRestore}>
                            {t('Restore from archive')}
                        </Button>
                    </div>
                )}
            </Message>
        </div>
    ) : null

    const hasErrors = () => {
        return false
        // const count = Object.keys(formError).length
        // if (count > 0) console.log('General.hasErrors', formError, count)
        // return count > 0
    }
    const isEqual = (a: any, b: any): boolean => {
        // Handle strict equality and reference equality fast path
        if (a === b) return true

        // Handle null / undefined
        if (a == null || b == null) return a === b

        // Handle Dates
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() === b.getTime()
        }

        // Handle Arrays (order-insensitive)
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false

            // Copy and sort for stable comparison
            const sortedA = [...a].sort()
            const sortedB = [...b].sort()

            return sortedA.every((val, idx) => isEqual(val, sortedB[idx]))
        }

        // Fallback for primitives and mismatched types
        return a === b
    }

    const onSubmit = (e: any) => {
        const excludedFields = ['createdAt', 'updatedAt']
        const isExcludedField = (key: string) => {
            return excludedFields.indexOf(key) !== -1
        }

        e.preventDefault()
        setFormError(null)

        const saveData = { ...formData }
        const id = saveData._id || ''

        // delete all not modified fields
        // but not if new record
        if (id && tab) {
            const keys = Object.keys(tab.store.initialData)
            keys.forEach((key) => {
                if (isEqual(tab.store.initialData[key], saveData[key]) || isExcludedField(key)) {
                    //console.log('delete ', key, saveData[key])
                    delete saveData[key]
                }
            })
        }
        console.log({ ...saveData })
        console.log(tab.store.initialData)

        // return if nothing has changed
        if (Object.keys(saveData).length <= 0) {
            return
        }

        formRef.current.checkAsync().then(async (result: any) => {
            if (!result.hasError) {
                const response = newRecord
                    ? await projectModel.create(saveData)
                    : await projectModel.update(id, saveData)
                if (response?.data) {
                    toastMessage(t('Successfully saved'))
                    const saved = { ...(formData || {}), ...(response.data || {}) }

                    // update UI + baseline so dirty becomes false
                    setFormData(saved)
                    tab.store.initialData = saved
                    // a rename shows in the tab too (a new record is renamed below)
                    if (!newRecord) setTabName(tabId, String(saved.name || '').trim())

                    if (newRecord) {
                        const newId = String(response.data._id || '')
                        const nextName = String(saved.name || '').trim()
                        const nextDataId = `project-${newId}`

                        // Convert tab immutably so FlexLayout header updates immediately
                        tabStore.tabs.value = tabStore.tabs.value.map((t) => {
                            if (t.id !== tabId) return t
                            return {
                                ...t,
                                dataId: nextDataId,
                                name: nextName,
                                store: {
                                    ...(t.store || {}),
                                    id: newId,
                                    resourceId: newId,
                                    formData: saved,
                                    initialData: saved,
                                    isDirty: false,
                                },
                            }
                        })
                    } else {
                        serverLog.info(`User ${user.value.userName} edited project ${response.data.name}`, {
                            actorID: user.value._id,
                            subjectID: response.data._id,
                        })
                    }
                    onCreated?.(response.data)
                    getSocket().emit('editing client: resourceUpdated', {
                        table: 'project',
                        id: String(response.data._id || tab.store.id),
                        url: 'project',
                    })
                } else {
                    // shown in the form, so taken out of the store — not a notice as well
                    setFormError(error.take() || t('Unknown Error'))
                }
            }
        })
    }

    const model = Schema.Model({
        _id: StringType(),
        name: StringType()
            .isRequired(format.required(t('Project name')))
            .addAsyncRule(
                (value, data) => {
                    return new Promise((resolve) => {
                        const _check = async () => {
                            let retCode = true
                            if (!newRecord || value) {
                                const id = data?._id || ''
                                if (id) {
                                    const result: any = await projectModel.getProjectByName(value)
                                    if (result.status === 404) retCode = true
                                    else if (result?.data && result.data._id != id) retCode = false
                                }
                            }
                            //console.log('addAsyncRule: name already exists = ', !retCode)
                            resolve(retCode)
                        }
                        _check()
                    })
                },
                t('Project name already in use') as string
            ),
        empntyNull: BooleanType(),
        clients: ArrayType()
            .isRequired(t('Client list cannot be empty'))
            .minLength(1, t('At least one client is required')),
        //.isRequired(t('User list cannot be empty'))
        //.minLength(1, t('At least one client is required')),
    })

    return (
        <Container>
            {/* <p>{'Test: ' + (newRecord ? 'New Record' : 'Edit Record')}</p> */}
            {/* <TabForm onFormSubmit={onApplyClick}/> */}

            <Stack className="tab-stack" spacing={20} alignItems="flex-start">
                <Panel bordered header={t('Project data')}>
                    {formError && (
                        <PanelError
                            message={formError}
                            showTitle={false}
                            style={{ marginTop: '-1em', marginBottom: '1em' }}
                        />
                    )}
                    <Form
                        layout='horizontal'
                        className="project-form subscription-form"
                        formValue={formData}
                        ref={formRef}
                        model={model}
                        onChange={(data) => {
                            //console.log('General.Form.onChange:', data)
                            console.log(data)
                            setFormData(data)
                        }}
                        onCheck={(err) => {
                            //setFormError(err)
                            // tabListHeaderChanged({
                            //   action: 'onCheck',
                            //   //data: formData,
                            //   //modified: true,
                            //   error: err,
                            // })
                        }}
                        autoComplete="off"
                        onSubmit={onSubmit}
                    >
                        <DisableOverlay disabled={!canEdit} banner={archiveBanner ?? lock.banner}>
                            <Container style={{ gap: '0.5em' }}>
                                <TextField
                                    name="name"
                                    label={t('Project name')}
                                    autoFocus={true}
                                //readOnly={!newRecord || readOnly}
                                />
                                <Form.Group controlId="description">
                                    <Form.Label>{t('Description')} </Form.Label>
                                    <Input
                                        name="description"
                                        as="textarea"
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                    />
                                </Form.Group>
                            </Container>

                            <hr />

                            {/* subscription */}
                            <Form.Group>
                                <div className="grid">
                                    <DateField name="startDate" label={t('Start date')} readOnly />
                                    <DateField
                                        name="endDate"
                                        label={t('End date')}
                                        placeholder={t('not finished yet')}
                                        readOnly
                                    />
                                </div>
                                <SelectField name="status" label={t('Project status')} data={statusList} />
                            </Form.Group>

                            <hr />

                            {/* clients */}
                            <Form.Control
                                name="clients"
                                accepter={ClientList}
                                value={formData.clients || []}
                                //error={formError?.['clients']}
                                readOnly={readOnly}
                                onChange={(val: string[]) => setFormData({ ...formData, clients: val })}
                            />

                            <hr />

                            {/* settings */}
                            {/* Outside the Form.Group on purpose. Inside it, the
                              * horizontal layout puts it in the label column, so
                              * it sat beside the settings like a field name. Out
                              * here it spans the section and reads as a title,
                              * the way the panel header does.
                              *
                              * Style measured off "Project data": 24px/400. The
                              * rs-panel-title class alone is not enough — its rule
                              * only applies inside rs-panel-header. */}
                            <div
                                className="rs-panel-title"
                                style={{ fontSize: '24px', fontWeight: 400, margin: '0 0 0.75rem' }}
                            >
                                {t('Settings')}
                            </div>

                            <Form.Group controlId="settings">

                                {/* The surrounding form lays out horizontally, so
                                  * without this column the settings line up side
                                  * by side. They need their own rows: ticking the
                                  * retention reveals a field beneath it, which
                                  * has nowhere to go on a shared line. */}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem 1rem',
                                    }}
                                >
                                <Form.Group controlId="emptyNull" className="checkbox">
                                    <Checkbox
                                        name="emptyNull"
                                        className="checkbox"
                                        checked={formData.emptyNull}
                                        onChange={(value, checked) => setFormData({ ...formData, emptyNull: checked })}
                                    >
                                        {t('Empty NULL Fields')}
                                    </Checkbox>
                                </Form.Group>

                                {/* Two controls for one value: the checkbox says
                                  * whether this project has an opinion at all,
                                  * the number says which. Null means "use the
                                  * tenant value" — an empty field would be
                                  * ambiguous next to a real zero.
                                  *
                                  * Saved with the rest of the form via Apply,
                                  * unlike the tenant setting, which writes as you
                                  * type. */}
                                <Form.Group controlId="taskRetentionOverride" className="checkbox">
                                    <Checkbox
                                        name="taskRetentionOverride"
                                        className="checkbox"
                                        disabled={readOnly}
                                        checked={formData.taskRetentionMinutes != null}
                                        onChange={(value, checked) =>
                                            setFormData({ ...formData, taskRetentionMinutes: checked ? 5 : null })
                                        }
                                    >
                                        {t('Own retention for finished tasks')}
                                    </Checkbox>
                                </Form.Group>

                                {formData.taskRetentionMinutes != null && (
                                    <Form.Group
                                        controlId="taskRetentionMinutes"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                    >
                                        {/* Centred against the input rather than
                                          * sitting on its top edge. */}
                                        <Form.Label style={{ margin: 0 }}>{t('Keep finished tasks for')} </Form.Label>
                                        <InputNumber
                                            name="taskRetentionMinutes"
                                            min={1}
                                            max={1440}
                                            disabled={readOnly}
                                            // The unit belongs to the value, not
                                            // to the label in brackets.
                                            postfix={t('minutes')}
                                            // On the wrapper, so the number keeps
                                            // room next to the unit — 5.5rem left
                                            // the input itself 9 pixels wide.
                                            style={{ width: '9rem' }}
                                            value={formData.taskRetentionMinutes}
                                            onChange={(value) =>
                                                setFormData({ ...formData, taskRetentionMinutes: Number(value) })
                                            }
                                        />
                                    </Form.Group>
                                )}
                                </div>
                            </Form.Group>

                            <div style={TabListFooterStyle}>
                                {hasErrors() && (
                                    <span style={TabListFooterErrorWrapperStyle}>
                                        <span style={TabListFooterErrorStyle}>{t('Check your inputs')}</span>
                                    </span>
                                )}
                                {hasPermission(Capabilities.project_write) && !archived && (
                                    <Button onClick={onSubmit} appearance="ghost">
                                        {t('Apply')}
                                    </Button>
                                )}
                            </div>
                        </DisableOverlay>
                    </Form>
                </Panel>

                {/*
                  * Its own panel, like "Project data" and "Assigned user".
                  * Each setting gets a row of its own: switching the retention on
                  * reveals a second field underneath it, which has nowhere to go
                  * when the checkboxes share a line.
                  */}
                {!newRecord && hasPermission(Capabilities.project_read_assigned_user) && (
                    <Panel bordered header={t('Assigned user')}>
                        <UserProjectRelation type={RelationType.User} id={tab?.store?.id} buttonText={t('Add user')} />
                    </Panel>
                )}
            </Stack>
        </Container>
    )
}

export default EditProject
